import queryString from "query-string";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteProps } from "react-router";
import { Grid, Dimmer, Header, Image, Loader } from "semantic-ui-react";
import {
  checkInvitationToken,
  checkInvitationTokenFailure as noInviteToken,
  acceptInvitation
} from "../actions/invitations/invitations";
import { getUserTypes } from "../actions/users/users";
import logo from "../assets/images/urbansim/logo.png";
import CustomFailure from "../components/common/CustomFailure";
import AcceptInvitation from "../components/invitations/AcceptInvitations";
import RegisterAccount from "../components/registrations/RegisterAccount";
import { amRoutes } from "../config/config";
import { RootState } from "../reducers";
import { history } from "../store";
import { hideSuccessMessage, hideFailureMessage } from "../store/statusMessages";
import { doesNotExist, exists } from "../utils/utils";

const ConfirmUserInviteView = ({ location }: RouteProps) => {
  const dispatch = useDispatch();
  const invitationToken = location?.search
    ? queryString.parse(location.search).invitation_token
    : null;

  useEffect(() => {
    if (doesNotExist(invitationToken)) {
      // @ts-ignore: Action not typed yet
      dispatch(noInviteToken("No invite token provided"));
    } else {
      dispatch(checkInvitationToken(invitationToken));
    }
    dispatch(getUserTypes());
  }, [dispatch, invitationToken]);

  const {
    userTypes,
    invitation,
    checkInvitationTokenStart,
    checkInvitationTokenSuccess,
    checkInvitationTokenFailure,
    checkInvitationTokenFailureMessage,
    acceptInvitationStart,
    showSuccessMessage,
    submitSucceededMessageHeader,
    submitSucceededMessageContent,
    showFailureMessage,
    submitFailedMessageHeader,
    submitFailedMessageContent
  } = useSelector(({ users, invitations, statusMessages }: RootState) => ({
    userTypes: users.userTypes,
    invitation: invitations.invitation,
    checkInvitationTokenStart: invitations.checkInvitationTokenStart,
    checkInvitationTokenSuccess: invitations.checkInvitationTokenSuccess,
    checkInvitationTokenFailure: invitations.checkInvitationTokenFailure,
    checkInvitationTokenFailureMessage: invitations.checkInvitationTokenFailureMessage,
    acceptInvitationStart: invitations.acceptInvitationStart,
    showSuccessMessage: statusMessages.showSuccessMessage,
    submitSucceededMessageHeader: statusMessages.submitSucceededMessageHeader,
    submitSucceededMessageContent: statusMessages.submitSucceededMessageContent,
    showFailureMessage: statusMessages.showFailureMessage,
    submitFailedMessageHeader: statusMessages.submitFailedMessageHeader,
    submitFailedMessageContent: statusMessages.submitFailedMessageContent
  }));
  let content;
  let style: React.CSSProperties = { maxWidth: 500 };
  if (checkInvitationTokenFailure) {
    style = {
      display: "flex",
      alignItems: "center"
    };
    content = (
      <>
        <Header as="h2" textAlign="center" style={{ marginTop: "-72px" }}>
          <Image src={logo} aria-label="Urbancanvas logo" /> UrbanSim
        </Header>
        <CustomFailure
          header="Unable to confirm account"
          content={checkInvitationTokenFailureMessage}
        />
      </>
    );
  } else if (checkInvitationTokenSuccess) {
    if (
      exists(invitation.invitee.registration) &&
      !exists(invitation.invitee.registration.confirmedAt)
    ) {
      content = (
        <RegisterAccount
          initialValues={invitation.invitee}
          onSubmit={(values: any) =>
            dispatch(acceptInvitation(Object.assign(values, { invitationToken: invitation.token })))
          }
          onCancel={() => {
            history.push(amRoutes.login);
            dispatch(hideSuccessMessage());
            dispatch(hideFailureMessage());
          }}
          publicRoles={undefined}
          userTypes={userTypes}
          loading={acceptInvitationStart}
          showSuccessMessage={showSuccessMessage}
          showFailureMessage={showFailureMessage}
          successMessageHeader={submitSucceededMessageHeader}
          successMessageContent={submitSucceededMessageContent}
          failureMessageHeader={submitFailedMessageHeader}
          failureMessageContent={submitFailedMessageContent}
        />
      );
    } else {
      style = { display: "inline" };
      content = (
        <AcceptInvitation
          loading={acceptInvitationStart}
          onAccept={() => dispatch(acceptInvitation({ invitationToken: invitation.token }))}
          showSuccessMessage={showSuccessMessage}
          successMessageHeader={submitSucceededMessageHeader}
          successMessageContent={submitSucceededMessageContent}
          invitation={invitation}
        />
      );
    }
  }
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        height: "100vh"
      }}
    >
      <Grid textAlign="center" style={{ height: "100%" }} verticalAlign="middle">
        {checkInvitationTokenStart && (
          <Dimmer active>
            <Loader />
          </Dimmer>
        )}
        <Grid.Column style={style}>{content}</Grid.Column>
      </Grid>
    </div>
  );
};

export default ConfirmUserInviteView;
